import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Md5 } from 'ts-md5';
import { Panel } from 'app/utils/panel';
import { map, noop, Observable, of } from 'rxjs';
import { extractUrlParamValue } from 'app/utils/helper';
import { LicenseState } from './license-state';

interface IAnalyticsSession {
    target_id: string;
    user_hash?: string;
    username?: string;
    journey_id?: string;
    variant_id?: string;
    created_at?: number;
}

interface IAnalyticsEvent {
    target_id: string;
    target_type: string;
    journey_id: string;
    user_hash: string;
    source: string;
    event_name: string;
    variant_id?: string;
    username?: string;
    system_id?: string;
    app: string;
}

class Session {
    constructor(public data: IAnalyticsSession) {}
}

export const CL_ANALYTICS_ENDPOINT = 'https://x-ray-advice.cloudlinux.com/api/analytics/v2';

@Injectable({ providedIn: 'root' })
export class ClAnalytics {
    private endpointEvent = '/events';
    private sessionTtl = 24 * 60 * 60 * 1000;

    constructor(
        private http: HttpClient,
        private panel: Panel,
        private licenseState: LicenseState,
        @Inject('Window') private window: Window,
        ) {
    }

    private get endpoint(): string {
        const endpointForOverride = localStorage.getItem('clAnalyticsMicroserviceEndpoint');
        return endpointForOverride || CL_ANALYTICS_ENDPOINT;
    }

    sendBuyButtonClickEvent(userName: string): Observable<void> {
        const event = 'awp_purchase_button_clicked';
        const session = this.generateNewSession(userName);

        return this.sendEvent(event, session.data);
    }

    sendMyImunifyPurchaseEvent(userName: string): Observable<void> {
        const event = 'awp_purchase_done';
        const session = this.getSession(userName) || this.generateNewSession(userName);

        return this.sendEvent(event, session.data);
    }

    private generateNewSession(userName: string): Session {
        const timestamp = Date.now();
        const search = this.window.location.search;
        const journey_id = extractUrlParamValue(search, 'journey_id');
        const user_hash = extractUrlParamValue(search, 'user_hash');
        const variant_id = extractUrlParamValue(search, 'variant_id');
        const target_id = extractUrlParamValue(search, 'target_id');
        const session = new Session({
            username: userName,
            target_id,
            user_hash: user_hash || Md5.hashStr(userName),
            journey_id: journey_id || Md5.hashStr(Math.floor(timestamp / 1000) + '|' + userName),
            variant_id: variant_id || '',
            created_at: timestamp,
        });
        this.saveSession(session);

        return session;
    }

    private saveSession(session: Session): void {
        const data = JSON.stringify(session.data);
        localStorage.setItem(`imAnalyticsSessionFor_${session.data.username}`, data);
    }

    private getSession(userName: string): Session | null {
        try {
            const data = JSON.parse(localStorage.getItem(`imAnalyticsSessionFor_${userName}`) || '');
            const _created_at = data.created_at ?? Date.now()
            const ttl = Date.now() - this.sessionTtl;
            if (_created_at >= ttl) {
                return new Session({ ...data });
            }
        } catch (e) {
            console.log(e);
        }

        return null;
    }

    private sendEvent(event: string, session: IAnalyticsSession): Observable<void> {
        if (!session.target_id) return of(undefined);

        const model: IAnalyticsEvent = {
            target_id: session.target_id,
            target_type: 'advice',
            journey_id: session.journey_id as string,
            user_hash: session.user_hash as string,
            source: this.panel.name.toLowerCase(),
            event_name: event,
            variant_id: session.variant_id,
            username: session.username,
            system_id: this.licenseState.license.getValue().id,
            app: 'imunify',
        };

        const report = Object.entries(model).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {});

        const endpoint = this.endpoint + this.endpointEvent;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        return this.http.post(endpoint, JSON.stringify(report), {headers: headers})
            .pipe(map(noop));
    }
}
